import { BaseService } from '../base/BaseService';
import type { ServiceResponse } from '../base/ServiceTypes';

import type { Json } from '@/integrations/supabase/types';

export interface TargetCoverAsset {
  id: string;
  publicUrl: string;
  origin: 'auto' | 'frame' | 'upload' | 'product';
  frameTimeMs: number | null;
  width: number;
  height: number;
  status: 'preparing' | 'ready' | 'failed' | 'stale';
}

export interface TargetCoverJob {
  id: string;
  targetId: string;
  sourceRevisionId: string;
  expectedTargetVersion: number;
  frameTimeMs: number;
  status: 'queued' | 'running' | 'ready' | 'failed';
  coverAssetId: string | null;
  safeErrorCode: string | null;
}

export interface TargetCoverState {
  targetId: string;
  revisionId: string;
  version: number;
  platform: string;
  placement: string;
  accountLabel: string | null;
  deliveryStatus: string;
  nativeOptions: Json;
  coverAsset: TargetCoverAsset | null;
}

export interface PostCoverTarget {
  id: string;
  platform: string;
  placement: string;
  nativeOptions: Json;
  readiness: Json;
}

export interface MaterializedPostTargets {
  targets: Array<{ id: string; platform: string; placement: string }>;
  ready: boolean;
  readinessIssue: string | null;
}

function jsonRecord(value: Json): Record<string, Json | undefined> {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, Json | undefined>)
    : {};
}

export function unresolvedPostCoverIssue(targets: PostCoverTarget[]): string | null {
  for (const target of targets) {
    const nativeOptions = jsonRecord(target.nativeOptions);
    const cover = jsonRecord(nativeOptions.cover ?? null);
    const readiness = jsonRecord(target.readiness);
    const mode = typeof cover.mode === 'string' ? cover.mode : 'provider_default';
    const coverReadiness = typeof readiness.cover === 'string' ? readiness.cover : null;
    const shareToFeed = nativeOptions.shareToFeed === true;
    const profileSurface = jsonRecord(nativeOptions.profileSurface ?? null);
    if (
      target.platform === 'instagram' &&
      target.placement === 'reel' &&
      shareToFeed &&
      profileSurface.ratio !== '3:4'
    ) {
      return 'Review how this cover appears on the Instagram profile grid.';
    }
    if (mode === 'provider_default') continue;
    if (coverReadiness === 'stale') {
      return `The ${target.platform} cover belongs to an earlier video revision.`;
    }
    if (coverReadiness !== 'ready') {
      return `The selected ${target.platform} cover is not ready.`;
    }
  }
  return null;
}

interface UploadedCover {
  coverAssetId: string;
  sourceRevisionId: string;
  publicUrl: string;
  width: number;
  height: number;
  mimeType: string;
}

class TargetCoverService extends BaseService {
  private static instance: TargetCoverService;

  static getInstance(): TargetCoverService {
    if (!TargetCoverService.instance) {
      TargetCoverService.instance = new TargetCoverService({ defaultTTL: 0, enableCache: false });
    }
    return TargetCoverService.instance;
  }

  private async accessToken(): Promise<string> {
    const { data, error } = await this.client.auth.getSession();
    if (error || !data.session?.access_token) throw new Error('Sign in to edit this cover');
    return data.session.access_token;
  }

  async materializePostTargets(postId: string): Promise<ServiceResponse<MaterializedPostTargets>> {
    return this.execute(async () => {
      const token = await this.accessToken();
      const response = await fetch('/api/social-targets/materialize', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ postId }),
      });
      const body = (await response.json()) as {
        targets?: Array<{ id: string; platform: string; placement: string }>;
        ready?: boolean;
        readinessIssue?: string | null;
        error?: string;
      };
      if (!response.ok) throw new Error(body.error ?? 'Could not prepare publishing targets');
      return {
        targets: body.targets ?? [],
        ready: body.ready === true,
        readinessIssue: body.readinessIssue ?? null,
      };
    }, 'Failed to prepare exact publishing targets');
  }

  async getPostTargets(postId: string): Promise<ServiceResponse<PostCoverTarget[]>> {
    return this.execute(async () => {
      await this.ensureAuthenticatedOwner();
      const { data, error } = await this.client
        .from('social_post_targets')
        .select('id, platform, placement, native_options_snapshot, readiness_snapshot')
        .eq('post_id', postId)
        .eq('readiness_snapshot->>active', 'true')
        .order('created_at');
      if (error) throw new Error(error.message);
      return (data ?? []).map((target) => ({
        id: target.id,
        platform: target.platform,
        placement: target.placement,
        nativeOptions: target.native_options_snapshot,
        readiness: target.readiness_snapshot,
      }));
    }, 'Failed to load exact publishing targets');
  }

  async getState(targetId: string): Promise<ServiceResponse<TargetCoverState | null>> {
    return this.execute(async () => {
      await this.ensureAuthenticatedOwner();
      const { data: target, error } = await this.client
        .from('social_post_targets')
        .select(
          'id, revision_id, connection_id, version, platform, placement, delivery_status, native_options_snapshot, cover_asset_id',
        )
        .eq('id', targetId)
        .maybeSingle();
      if (error) throw new Error(error.message);
      if (!target) return null;

      const { data: connection, error: connectionError } = await this.client
        .from('user_connections')
        .select('display_name, platform_username, platform_user_id')
        .eq('id', target.connection_id)
        .maybeSingle();
      if (connectionError) throw new Error(connectionError.message);

      let coverAsset: TargetCoverAsset | null = null;
      if (target.cover_asset_id) {
        const { data: asset, error: assetError } = await this.client
          .from('social_cover_assets')
          .select('id, public_url, origin, frame_time_ms, width, height, status')
          .eq('id', target.cover_asset_id)
          .maybeSingle();
        if (assetError) throw new Error(assetError.message);
        if (asset) {
          coverAsset = {
            id: asset.id,
            publicUrl: asset.public_url,
            origin: asset.origin as TargetCoverAsset['origin'],
            frameTimeMs: asset.frame_time_ms,
            width: asset.width,
            height: asset.height,
            status: asset.status as TargetCoverAsset['status'],
          };
        }
      }
      return {
        targetId: target.id,
        revisionId: target.revision_id,
        version: target.version,
        platform: target.platform,
        placement: target.placement,
        accountLabel:
          connection?.display_name ??
          connection?.platform_username ??
          connection?.platform_user_id ??
          null,
        deliveryStatus: target.delivery_status,
        nativeOptions: target.native_options_snapshot,
        coverAsset,
      };
    }, 'Failed to load the target cover');
  }

  async getRevisionAssets(revisionId: string): Promise<ServiceResponse<TargetCoverAsset[]>> {
    return this.execute(async () => {
      await this.ensureAuthenticatedOwner();
      const { data, error } = await this.client
        .from('social_cover_assets')
        .select('id, public_url, origin, frame_time_ms, width, height, status')
        .eq('source_revision_id', revisionId)
        .eq('status', 'ready')
        .order('created_at', { ascending: false });
      if (error) throw new Error(error.message);
      return (data ?? []).map((asset) => ({
        id: asset.id,
        publicUrl: asset.public_url,
        origin: asset.origin as TargetCoverAsset['origin'],
        frameTimeMs: asset.frame_time_ms,
        width: asset.width,
        height: asset.height,
        status: asset.status as TargetCoverAsset['status'],
      }));
    }, 'Failed to load cover choices');
  }

  async getAsset(assetId: string): Promise<ServiceResponse<TargetCoverAsset | null>> {
    return this.execute(async () => {
      await this.ensureAuthenticatedOwner();
      const { data, error } = await this.client
        .from('social_cover_assets')
        .select('id, public_url, origin, frame_time_ms, width, height, status')
        .eq('id', assetId)
        .maybeSingle();
      if (error) throw new Error(error.message);
      return data
        ? {
            id: data.id,
            publicUrl: data.public_url,
            origin: data.origin as TargetCoverAsset['origin'],
            frameTimeMs: data.frame_time_ms,
            width: data.width,
            height: data.height,
            status: data.status as TargetCoverAsset['status'],
          }
        : null;
    }, 'Failed to load the prepared cover');
  }

  async prepareFrame(input: {
    targetId: string;
    expectedTargetVersion: number;
    frameTimeMs: number;
    idempotencyKey: string;
    correlationId: string;
  }): Promise<ServiceResponse<{ jobId: string; status: TargetCoverJob['status'] }>> {
    return this.execute(async () => {
      const token = await this.accessToken();
      const response = await fetch('/api/target-cover/frame', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(input),
      });
      const body = (await response.json()) as {
        jobId?: string;
        status?: TargetCoverJob['status'];
        error?: string;
      };
      if (!response.ok || !body.jobId) throw new Error(body.error ?? 'Could not prepare frame');
      return { jobId: body.jobId, status: body.status ?? 'queued' };
    }, 'Failed to prepare the target cover frame');
  }

  async getJob(jobId: string): Promise<ServiceResponse<TargetCoverJob | null>> {
    return this.execute(async () => {
      await this.ensureAuthenticatedOwner();
      const { data, error } = await this.client
        .from('social_cover_jobs')
        .select(
          'id, target_id, source_revision_id, expected_target_version, frame_time_ms, status, cover_asset_id, safe_error_code',
        )
        .eq('id', jobId)
        .maybeSingle();
      if (error) throw new Error(error.message);
      if (!data) return null;
      return {
        id: data.id,
        targetId: data.target_id,
        sourceRevisionId: data.source_revision_id,
        expectedTargetVersion: data.expected_target_version,
        frameTimeMs: data.frame_time_ms,
        status: data.status as TargetCoverJob['status'],
        coverAssetId: data.cover_asset_id,
        safeErrorCode: data.safe_error_code,
      };
    }, 'Failed to load the cover job');
  }

  async uploadCover(input: {
    targetId: string;
    expectedTargetVersion: number;
    file: File;
    idempotencyKey: string;
  }): Promise<ServiceResponse<UploadedCover>> {
    return this.execute(async () => {
      const token = await this.accessToken();
      if (!['image/jpeg', 'image/png', 'image/webp'].includes(input.file.type)) {
        throw new Error('Use a JPEG, PNG, or WebP image up to 8 MB.');
      }
      if (input.file.size <= 0 || input.file.size > 8 * 1024 * 1024) {
        throw new Error('Use a JPEG, PNG, or WebP image up to 8 MB.');
      }
      const prepareResponse = await fetch('/api/target-cover/upload', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          action: 'prepare',
          targetId: input.targetId,
          expectedTargetVersion: input.expectedTargetVersion,
          mimeType: input.file.type,
          byteSize: input.file.size,
          idempotencyKey: input.idempotencyKey,
        }),
      });
      const prepared = (await prepareResponse.json()) as {
        bucket?: string;
        objectPath?: string;
        signedToken?: string;
        error?: string;
      };
      if (
        !prepareResponse.ok ||
        !prepared.bucket ||
        !prepared.objectPath ||
        !prepared.signedToken
      ) {
        throw new Error(prepared.error ?? 'Cover upload could not start');
      }

      const { error: uploadError } = await this.client.storage
        .from(prepared.bucket)
        .uploadToSignedUrl(prepared.objectPath, prepared.signedToken, input.file, {
          contentType: input.file.type,
          upsert: true,
        });
      if (uploadError) throw new Error('Cover upload failed');

      const finalizeResponse = await fetch('/api/target-cover/upload', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          action: 'finalize',
          targetId: input.targetId,
          expectedTargetVersion: input.expectedTargetVersion,
          mimeType: input.file.type,
          idempotencyKey: input.idempotencyKey,
        }),
      });
      const finalized = (await finalizeResponse.json()) as UploadedCover & { error?: string };
      if (!finalizeResponse.ok || !finalized.coverAssetId) {
        throw new Error(finalized.error ?? 'Cover upload failed');
      }
      return finalized;
    }, 'Failed to upload the target cover');
  }

  async saveNativeOptions(input: {
    targetId: string;
    expectedTargetVersion: number;
    patch: Json;
    idempotencyKey: string;
    correlationId: string;
  }): Promise<ServiceResponse<Json>> {
    return this.execute(async () => {
      await this.ensureAuthenticatedOwner();
      const { data, error } = await this.client.rpc('update_social_target_native_options', {
        p_target_id: input.targetId,
        p_expected_target_version: input.expectedTargetVersion,
        p_patch: input.patch,
        p_idempotency_key: input.idempotencyKey,
        p_correlation_id: input.correlationId,
      });
      if (error) throw new Error(error.message);
      return data;
    }, 'Failed to save the target cover');
  }
}

export const targetCoverService = TargetCoverService.getInstance();
export { TargetCoverService };
